"use client";
import { getApiBaseUrl } from "@/lib/apiBase";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

const BASE_URL = getApiBaseUrl();

export default function FeaturesPage() {
  const [selected, setSelected] = useState(null); // id of selected item or null
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchMarketing = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/marketing?type=marketing`);
        const data = await response.json();
        
        // Transform API data to match component structure
        const transformedItems = data.map((item) => ({
          id: item.id,
          title: item.title,
          short: item.short_description || "",
          long: item.long_description || "",
          img: item.image ? `${BASE_URL}${item.image}` : "https://picsum.photos/600/400?random=" + item.id,
        }));
        
        setItems(transformedItems);
      } catch (error) {
        console.error("Error fetching marketing data:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketing();
  }, []);

  const handleSelect = (id) => {
    setSelected((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse mb-6">
            <div className="h-8 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="aspect-[4/3] w-full bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                      <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0 && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2">Хамтран ажиллах санал, нэмэлт үйлчилгээнүүд</h1>
          <h3 className="text-sm mb-6">Бид таны хэрэгцээнд нийцүүлэн сонгох боломжтой нэмэлт үйлчилгээг санал болгож байна. Хүссэн үедээ өөрчлөх, нэмэх, цуцлах уян хатан байдалтай.</h3>
          <div className="text-center py-12">
            <p className="text-gray-500">Одоогоор мэдээлэл байхгүй байна.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-semibold">Хамтран ажиллах санал, нэмэлт үйлчилгээнүүд</h1>
        <h3 className="text-3xl text-sm mb-6">Бид таны хэрэгцээнд нийцүүлэн сонгох боломжтой нэмэлт үйлчилгээг санал болгож байна. Хүссэн үедээ өөрчлөх, нэмэх, цуцлах уян хатан байдалтай.</h3>
        {/* If nothing selected — show 4 equal columns grid */}
        {!selected && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {items.map((it) => (
              <motion.button
                key={it.id}
                onClick={() => handleSelect(it.id)}
                className="group relative overflow-hidden rounded-xl shadow hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 bg-white text-left"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                layout
              >
                <div className="aspect-[4/3] w-full relative">
                  <Image
                    src={it.img}
                    alt={it.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    unoptimized
                  />
                </div>
                <div className="p-4">
                  <h2 className="text-lg font-medium">{it.title}</h2>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-3">{it.short}</p>
                </div>
              </motion.button>
            ))}
          </div>
        )}

        {/* When selected: left column shows the clicked card, right area shows expanded details */}
        <AnimatePresence>
          {selected && (
            <motion.div
              key={selected}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start"
            >
              {/* Left: clicked card */}
              <motion.div layout className="lg:col-span-1 order-1">
                {items
                  .filter((i) => i.id === selected)
                  .map((it) => (
                    <div
                      key={it.id}
                      className="relative overflow-hidden rounded-xl shadow-lg bg-white"
                    >
                      <button
                        onClick={() => handleSelect(it.id)}
                        className="w-full text-left"
                        aria-label={`Select ${it.title}`}
                      >
                        <div className="aspect-[4/3] w-full relative">
                          <Image
                            src={it.img}
                            alt={it.title}
                            fill
                            className="object-cover"
                            unoptimized
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-xl font-semibold">{it.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{it.short}</p>
                        </div>
                      </button>
                      <div className="absolute top-3 right-3 flex gap-2">
                        <button
                          onClick={() => setSelected(null)}
                          className="bg-white/80 hover:bg-white px-2 py-1 rounded shadow text-sm"
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
              </motion.div>

              {/* Right: expanded content */}
              <motion.div layout className="lg:col-span-3 order-2">
                {items
                  .filter((i) => i.id === selected)
                  .map((it) => (
                    <motion.article
                      key={it.id}
                      className="rounded-xl bg-white shadow-lg overflow-hidden"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="relative h-64">
                        <Image
                          src={it.img}
                          alt={it.title}
                          fill
                          className="object-cover"
                          unoptimized
                        />
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background:
                              "linear-gradient(180deg,#000000b3,#0000004d 72.92%,#0000)",
                          }}
                        />
                        <div className="absolute left-6 bottom-6 text-white">
                          <h2 className="text-2xl font-bold drop-shadow">{it.title}</h2>
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-gray-700 leading-relaxed">{it.long}</p>
                      </div>
                    </motion.article>
                  ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
